import { faEdit } from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { BulmaButton, CancelButton, SubmitButton } from "Components/Button";
import { CollapsibleSection } from "Components/CollapsibleSection";
import { Horizontal } from "Components/Horizontal";
import { ButtonIcon } from "Components/Icon";
import { Modal } from "Components/Modal";
import { ApplicationStatus } from "generated/graphql";
import { AddressInput } from "Inputs/AddressInput";
import { EnterKeyCatcher } from "Inputs/EnterKeyCatcher";
import { useFormWithSchema } from "Inputs/FormTools";
import { PhoneInput as PhoneTextbox } from "Inputs/PhoneInput";
import { Textbox } from "Inputs/Textbox";
import { YupDefined } from "Inputs/Tools";
import * as React from "react";
import { formatPhoneNumber, hasValue, no } from "Tools";
import * as Yup from "yup";
import { Application } from "./Types";

export function LossPayeeCollapsible(props: {
  app: Application;
  refetch: () => void;
}) {
  const [isEditing, setIsEditing] = React.useState(false);

  return (
    <CollapsibleSection
      title={<>Loss Payee: {props.app.lossPayee?.name ?? "TBD"}</>}
      theme={{ header: { className: "is-secondary" } }}
    >
      {hasValue(props.app.lossPayee) ? (
        <>
          <div>{props.app.lossPayee.name}</div>
          <div>{props.app.lossPayee.mailingAddress1}</div>
          <div>{props.app.lossPayee.mailingAddress2}</div>
          <div>
            {props.app.lossPayee.city}, {props.app.lossPayee.state}{" "}
            {props.app.lossPayee.zipCode}
          </div>
          <div>{formatPhoneNumber(props.app.lossPayee.phoneNumber)}</div>
          <EditLossPayeeModal
            isVisible={isEditing}
            handleClose={() => setIsEditing(false)}
            app={props.app}
            refetch={props.refetch}
          />
          <div className="has-text-centered">
            <BulmaButton
              className="is-primary"
              disabled={props.app.status === ApplicationStatus.CANCELED}
              onClick={() => setIsEditing(true)}
            >
              <ButtonIcon icon={faEdit} />
              <span>Edit Loss Payee</span>
            </BulmaButton>
          </div>
        </>
      ) : (
        <div className="has-text-centered">
          <BulmaButton
            className="is-primary"
            disabled={props.app.status === ApplicationStatus.CANCELED}
            onClick={() => window.location.assign(props.app.addLossPayeeLink)}
          >
            <span>Add Loss Payee</span>
          </BulmaButton>
        </div>
      )}
    </CollapsibleSection>
  );
}

function EditLossPayeeModal(props: {
  isVisible: boolean;
  handleClose: () => void;
  app: Application;
  refetch: () => void;
}) {
  const formik = useForm({
    app: props.app,
    handleClose: props.handleClose,
    refetch: props.refetch,
  });

  return (
    <Modal
      title="Edit Loss Payee"
      isVisible={props.isVisible}
      buttonRow={[
        <SubmitButton
          key="submit"
          isSubmitting={formik.isSubmitting}
          onClick={formik.submitForm}
          disabled={!formik.dirty}
        />,
        <CancelButton key="cancel" onClick={props.handleClose} />,
      ]}
      onCancel={props.handleClose}
    >
      <EnterKeyCatcher handleSubmit={() => formik.handleSubmit()}>
        <p>
          <em>
            A revised Loss Payee Endorsement will be emailed to{" "}
            {props.app.applicant.fullName} at {props.app.applicant.emailAddress}
            .
          </em>
        </p>
        <Horizontal label="Name" isRequired>
          <div className="field">
            <Textbox
              fieldProps={formik.field.payeeName}
              placeholder="Name"
              style={{ flexGrow: 1 }}
            />
          </div>
        </Horizontal>
        <AddressInput field={formik.field.address} />
        <Horizontal label="Phone" isRequired>
          <PhoneTextbox fieldProps={formik.field.phoneNumber} />
        </Horizontal>
      </EnterKeyCatcher>
    </Modal>
  );
}

function useForm(options: {
  app: Application;
  handleClose: () => void;
  refetch: () => void;
}) {
  const lossPayee = options.app.lossPayee ?? no();
  const initialValues = {
    phoneNumber: lossPayee.phoneNumber,
    payeeName: lossPayee.name,
    address: {
      ...lossPayee,
      address1: lossPayee.mailingAddress1,
      address2: lossPayee.mailingAddress2 ?? null,
    },
  };
  return useFormWithSchema({
    initialValues,
    onSubmit: async values => {
      const submitValues = {
        name: values.payeeName,
        mailingAddress1: values.address.address1,
        mailingAddress2: values.address.address2,
        city: values.address.city,
        state: values.address.state,
        zipCode: values.address.zipCode,
        phoneNumber: values.phoneNumber,
      };
      await axios.put(
        `/api/lossPayee/${options.app.securityKey}`,
        submitValues,
      );
      options.refetch();
      options.handleClose();
    },
    validationSchema: Yup.object({
      payeeName: YupDefined.string()
        .nullable()
        .max(255)
        .label("Loss Payee Name")
        .required(),
      address: AddressInput.getDefaultValidator(),
      phoneNumber: PhoneTextbox.getDefaultValidator({ isRequired: true }),
    }),
  });
}
